import {
  RARITY_PRESENTATION,
  assignTechniqueToLoadoutSlot,
  maxEquippedTechniques,
  nextTechniqueScrollRarity,
  removeTechniqueFromLoadoutSlot,
  type TechniqueLoadoutSlot,
} from "@ninja/domain";
import { TECHNIQUES } from "@ninja/game-content";
import { TECHNIQUE_ICONS } from "@ninja/asset-catalog";

export type JutsuLoadoutSlot = TechniqueLoadoutSlot;

export interface JutsuLibraryEntry {
  techniqueId: string;
  techniqueLevel: number;
}

export interface JutsuLibraryData {
  known: JutsuLibraryEntry[];
  equipped: JutsuLoadoutSlot[];
}

interface JutsuLoadoutTabOptions {
  ninjaId: string;
  ninjaRarity: keyof typeof RARITY_PRESENTATION;
  canEdit: boolean;
  unavailableMessage: string | null;
  isActive: () => boolean;
  load: (ninjaId: string) => Promise<JutsuLibraryData>;
  save: (
    ninjaId: string,
    slots: JutsuLoadoutSlot[],
  ) => Promise<JutsuLoadoutSlot[]>;
}

const TECHNIQUE_FAMILY_LABELS: Record<string, string> = {
  taijutsu: "Taijutsu",
  elemental_ninjutsu: "Elemental Ninjutsu",
  fuinjutsu: "Fūinjutsu",
  medical_ninjutsu: "Medical Ninjutsu",
  genjutsu: "Genjutsu",
};

const SCROLL_RARITIES = ["uncommon", "rare", "epic", "legendary"] as const;

export class JutsuLoadoutTab {
  private data: JutsuLibraryData | null = null;
  private loading = false;
  private loadError = "";
  private saving = false;
  private selectedSlot = 0;
  private searchText = "";
  private selectedFamily = "all";
  private selectedScrollRarity = "all";

  constructor(
    private readonly container: HTMLElement,
    private readonly options: JutsuLoadoutTabOptions,
  ) {}

  async activate(): Promise<void> {
    if (this.data) {
      this.render();
      return;
    }
    if (this.loading) return;
    this.loading = true;
    this.container.innerHTML =
      '<div class="empty-state jutsu-loading">Loading this ninja\'s technique library…</div>';
    try {
      this.data = await this.options.load(this.options.ninjaId);
      this.loadError = "";
      this.selectedSlot = Math.min(
        this.selectedSlot,
        maxEquippedTechniques(this.options.ninjaRarity) - 1,
      );
      if (this.container.isConnected && this.options.isActive()) this.render();
    } catch (error) {
      this.loadError = (error as Error).message;
      if (this.container.isConnected && this.options.isActive())
        this.renderLoadError();
    } finally {
      this.loading = false;
    }
  }

  private renderLoadError(): void {
    this.container.innerHTML = `<div class="empty-state jutsu-load-error"><p>${escapeHtml(this.loadError)}</p><button type="button" class="button button-outline" data-retry-jutsu-library>Retry</button></div>`;
    this.container
      .querySelector<HTMLButtonElement>("[data-retry-jutsu-library]")
      ?.addEventListener("click", () => void this.activate());
  }

  private render(): void {
    const data = this.data;
    if (!data) return;
    const capacity = maxEquippedTechniques(this.options.ninjaRarity);
    const selected = data.equipped.find(
      ({ slotIndex }) => slotIndex === this.selectedSlot,
    );
    const selectedTechnique = selected
      ? data.known.find(
          ({ techniqueId }) => techniqueId === selected.techniqueId,
        )
      : undefined;
    const selectedDefinition = selectedTechnique
      ? TECHNIQUES[selectedTechnique.techniqueId]
      : undefined;

    this.container.innerHTML = `<section class="jutsu-library-panel">
      <p class="inventory-tab-intro">The library lists techniques this ninja already knows. Rarity determines the number of active slots; removing a jutsu from the bar does not erase its mastery.</p>
      ${this.options.unavailableMessage ? `<p class="jutsu-availability-note" role="status">${escapeHtml(this.options.unavailableMessage)} Loadout changes are disabled.</p>` : ""}
      <div class="jutsu-slot-grid" role="group" aria-label="Jutsu bar slots">${Array.from({ length: capacity }, (_, slotIndex) => this.renderSlot(slotIndex, data)).join("")}</div>
      <div class="jutsu-selected-slot"><div><span class="eyebrow">SELECTED SLOT</span><strong>Slot ${this.selectedSlot + 1}${selectedDefinition ? ` · ${escapeHtml(selectedDefinition.name)}` : " · Empty"}</strong></div><button type="button" class="button button-outline" data-clear-jutsu-slot ${!selected || !this.options.canEdit || this.saving ? "disabled" : ""}>Unequip selected</button></div>
      <div class="jutsu-library-toolbar"><label><span>SEARCH</span><input type="search" data-jutsu-search value="${escapeHtml(this.searchText)}" placeholder="Search known jutsus" /></label><label><span>TYPE</span><select data-jutsu-family><option value="all">All types</option>${this.familyOptions(data)}</select></label><label><span>NEXT SCROLL</span><select data-jutsu-scroll><option value="all">All rarities</option>${this.scrollOptions()}</select></label></div>
      <div class="jutsu-library-grid" role="list" aria-label="Known jutsus"></div>
      <p class="jutsu-feedback" role="status" aria-live="polite"></p>
    </section>`;
    this.bindTechniqueIconFallbacks(this.container);

    const search = this.container.querySelector<HTMLInputElement>(
      "[data-jutsu-search]",
    )!;
    const family = this.container.querySelector<HTMLSelectElement>(
      "[data-jutsu-family]",
    )!;
    const scrollRarity = this.container.querySelector<HTMLSelectElement>(
      "[data-jutsu-scroll]",
    )!;
    family.value = this.selectedFamily;
    scrollRarity.value = this.selectedScrollRarity;
    search.addEventListener("input", () => {
      this.searchText = search.value;
      this.renderLibrary();
    });
    family.addEventListener("change", () => {
      this.selectedFamily = family.value;
      this.renderLibrary();
    });
    scrollRarity.addEventListener("change", () => {
      this.selectedScrollRarity = scrollRarity.value;
      this.renderLibrary();
    });

    this.container
      .querySelectorAll<HTMLButtonElement>("[data-jutsu-slot]")
      .forEach((button) =>
        button.addEventListener("click", () => {
          this.selectedSlot = Number(button.dataset.jutsuSlot);
          this.render();
        }),
      );
    this.container
      .querySelector<HTMLButtonElement>("[data-clear-jutsu-slot]")
      ?.addEventListener("click", () => {
        if (!selected) return;
        const definition = TECHNIQUES[selected.techniqueId];
        const name =
          definition?.name ?? displayTechniqueId(selected.techniqueId);
        this.openConfirmation(
          `Remove ${name} from Slot ${this.selectedSlot + 1}?`,
          "The technique will remain in this ninja's library and can be equipped again later.",
          "Unequip jutsu",
          async () => {
            const remaining = removeTechniqueFromLoadoutSlot(
              data.equipped,
              this.selectedSlot,
            );
            await this.persist(remaining, "Jutsu removed from the active bar.");
          },
        );
      });
    this.renderLibrary();
  }

  private renderSlot(slotIndex: number, data: JutsuLibraryData): string {
    const equipped = data.equipped.find(
      (entry) => entry.slotIndex === slotIndex,
    );
    const known = equipped
      ? data.known.find(
          ({ techniqueId }) => techniqueId === equipped.techniqueId,
        )
      : undefined;
    const definition = known ? TECHNIQUES[known.techniqueId] : undefined;
    const icon = known ? techniqueIcon(known.techniqueId) : undefined;
    const rarity = known
      ? nextTechniqueScrollRarity(known.techniqueLevel)
      : null;
    const rarityColor = rarity ? RARITY_PRESENTATION[rarity].color : "#657265";
    return `<button type="button" class="jutsu-slot-tile ${slotIndex === this.selectedSlot ? "selected" : ""} ${equipped ? "occupied" : "empty"}" data-jutsu-slot="${slotIndex}" aria-pressed="${slotIndex === this.selectedSlot}" style="--jutsu-rarity:${rarityColor}"><span class="jutsu-slot-index">SLOT ${slotIndex + 1}</span>${icon ? this.techniqueIconMarkup(icon, "jutsu-slot-icon") : `<span class="jutsu-slot-icon jutsu-slot-empty-icon">${equipped ? "?" : "+"}</span>`}<strong>${definition ? escapeHtml(definition.name) : "Empty"}</strong><small>${known ? `Lv ${known.techniqueLevel}` : "Choose a slot"}</small></button>`;
  }

  private familyOptions(data: JutsuLibraryData): string {
    const families = new Set<string>(
      data.known.flatMap(({ techniqueId }) => {
        const family = TECHNIQUES[techniqueId]?.family;
        return family ? [family] : [];
      }),
    );
    return [...families]
      .sort()
      .map(
        (family) =>
          `<option value="${escapeHtml(family)}">${escapeHtml(TECHNIQUE_FAMILY_LABELS[family] ?? family)}</option>`,
      )
      .join("");
  }

  private scrollOptions(): string {
    return (
      SCROLL_RARITIES.map(
        (rarity) =>
          `<option value="${rarity}">${RARITY_PRESENTATION[rarity].label}</option>`,
      ).join("") + '<option value="max">Max level</option>'
    );
  }

  private renderLibrary(): void {
    const data = this.data;
    if (!data) return;
    const grid = this.container.querySelector<HTMLElement>(
      ".jutsu-library-grid",
    );
    if (!grid) return;
    const normalizedQuery = this.searchText.trim().toLocaleLowerCase();
    const matches = data.known.filter((entry) => {
      const definition = TECHNIQUES[entry.techniqueId];
      const name = definition?.name ?? displayTechniqueId(entry.techniqueId);
      const family = definition?.family ?? "unknown";
      const nextRarity = nextTechniqueScrollRarity(entry.techniqueLevel);
      const rarityKey = nextRarity ?? "max";
      return (
        name.toLocaleLowerCase().includes(normalizedQuery) &&
        (this.selectedFamily === "all" || family === this.selectedFamily) &&
        (this.selectedScrollRarity === "all" ||
          rarityKey === this.selectedScrollRarity)
      );
    });
    grid.innerHTML = matches.length
      ? matches.map((entry) => this.renderTechniqueCard(entry, data)).join("")
      : '<div class="empty-state jutsu-library-empty">No known jutsus match these filters.</div>';
    this.bindTechniqueIconFallbacks(grid);
    grid
      .querySelectorAll<HTMLButtonElement>("[data-known-technique]")
      .forEach((button) =>
        button.addEventListener("click", () => {
          const entry = data.known.find(
            ({ techniqueId }) => techniqueId === button.dataset.knownTechnique,
          );
          if (entry) this.confirmEquip(entry, data);
        }),
      );
  }

  private renderTechniqueCard(
    entry: JutsuLibraryEntry,
    data: JutsuLibraryData,
  ): string {
    const definition = TECHNIQUES[entry.techniqueId];
    const name = definition?.name ?? displayTechniqueId(entry.techniqueId);
    const family = definition?.family ?? "unknown";
    const nextRarity = nextTechniqueScrollRarity(entry.techniqueLevel);
    const rarityColor = nextRarity
      ? RARITY_PRESENTATION[nextRarity].color
      : "#657265";
    const icon = techniqueIcon(entry.techniqueId);
    const equippedSlot = data.equipped.find(
      ({ techniqueId }) => techniqueId === entry.techniqueId,
    )?.slotIndex;
    const alreadyInSelectedSlot = equippedSlot === this.selectedSlot;
    const nextLabel = nextRarity
      ? `Next: ${RARITY_PRESENTATION[nextRarity].label} scroll`
      : "Max level";
    const familyLabel = TECHNIQUE_FAMILY_LABELS[family] ?? family;
    const equippedLabel =
      equippedSlot === undefined
        ? ""
        : `<small class="jutsu-equipped-state">${alreadyInSelectedSlot ? `Equipped in Slot ${equippedSlot + 1}` : `Move from Slot ${equippedSlot + 1}`}</small>`;
    return `<button type="button" class="jutsu-library-card ${alreadyInSelectedSlot ? "equipped-here" : ""}" data-known-technique="${escapeHtml(entry.techniqueId)}" role="listitem" style="--jutsu-rarity:${rarityColor}" ${!this.options.canEdit || alreadyInSelectedSlot || this.saving ? "disabled" : ""} title="${alreadyInSelectedSlot ? "Already equipped in this slot" : this.options.canEdit ? "Choose this jutsu for the selected slot" : escapeHtml(this.options.unavailableMessage ?? "This ninja cannot change loadout right now.")}"><span class="jutsu-library-icon">${icon ? this.techniqueIconMarkup(icon) : '<span class="jutsu-icon-fallback">?</span>'}</span><span class="jutsu-library-copy"><strong>${escapeHtml(name)}</strong><small>${escapeHtml(familyLabel)} · ${escapeHtml(definition?.proficiency ?? "unknown")}</small><small>Technique level ${entry.techniqueLevel}</small></span><span class="jutsu-next-scroll">${escapeHtml(nextLabel)}</span>${equippedLabel}</button>`;
  }

  private techniqueIconMarkup(
    icon: TechniqueIconAsset,
    className?: string,
  ): string {
    return `<img ${className ? `class="${className}"` : ""} data-technique-icon src="/${icon.path}" ${icon.fallbackPath ? `data-fallback-path="/${icon.fallbackPath}"` : ""} alt="" loading="lazy" />`;
  }

  private bindTechniqueIconFallbacks(root: ParentNode): void {
    root
      .querySelectorAll<HTMLImageElement>("img[data-technique-icon]")
      .forEach((image) => {
        image.addEventListener("error", () => {
          const fallback = image.dataset.fallbackPath;
          if (fallback && image.dataset.fallbackTried !== "true") {
            image.dataset.fallbackTried = "true";
            image.src = fallback;
            return;
          }
          image.hidden = true;
        });
      });
  }

  private confirmEquip(entry: JutsuLibraryEntry, data: JutsuLibraryData): void {
    const definition = TECHNIQUES[entry.techniqueId];
    const name = definition?.name ?? displayTechniqueId(entry.techniqueId);
    const replaced = data.equipped.find(
      ({ slotIndex }) => slotIndex === this.selectedSlot,
    );
    const movingFrom = data.equipped.find(
      ({ techniqueId }) => techniqueId === entry.techniqueId,
    );
    const replacedName = replaced
      ? (TECHNIQUES[replaced.techniqueId]?.name ??
        displayTechniqueId(replaced.techniqueId))
      : null;
    let message = `Equip ${name} in Slot ${this.selectedSlot + 1}?`;
    if (movingFrom && movingFrom.slotIndex !== this.selectedSlot) {
      message = `Move ${name} from Slot ${movingFrom.slotIndex + 1} to Slot ${this.selectedSlot + 1}${replacedName ? `, replacing ${replacedName}` : ""}?`;
    } else if (replacedName && replaced?.techniqueId !== entry.techniqueId) {
      message = `Equip ${name} in Slot ${this.selectedSlot + 1}, replacing ${replacedName}?`;
    }
    this.openConfirmation(
      message,
      "The server will save this ordered jutsu bar. Any replaced jutsu remains in the ninja's library.",
      "Confirm jutsu",
      async () => {
        const slots = assignTechniqueToLoadoutSlot(
          data.equipped,
          this.selectedSlot,
          entry.techniqueId,
        );
        await this.persist(
          slots,
          `${name} assigned to Slot ${this.selectedSlot + 1}.`,
        );
      },
    );
  }

  private openConfirmation(
    title: string,
    description: string,
    confirmLabel: string,
    confirm: () => Promise<void>,
  ): void {
    if (!this.options.canEdit) return;
    this.container.querySelector(".jutsu-confirm-backdrop")?.remove();
    const dialog = document.createElement("div");
    dialog.className = "jutsu-confirm-backdrop";
    dialog.innerHTML = `<section class="jutsu-confirm-dialog" role="alertdialog" aria-modal="true" aria-labelledby="jutsu-confirm-title"><p class="eyebrow">CONFIRM JUTSU BAR</p><h3 id="jutsu-confirm-title">${escapeHtml(title)}</h3><p>${escapeHtml(description)}</p><div class="jutsu-confirm-actions"><button type="button" class="button button-outline" data-cancel-jutsu>Cancel</button><button type="button" class="button button-primary" data-confirm-jutsu>${escapeHtml(confirmLabel)}</button></div><p class="jutsu-confirm-feedback" role="status"></p></section>`;
    this.container.append(dialog);
    const close = (): void => dialog.remove();
    dialog
      .querySelector<HTMLButtonElement>("[data-cancel-jutsu]")
      ?.addEventListener("click", () => {
        if (!this.saving) close();
      });
    dialog.addEventListener("click", (event) => {
      if (event.target === dialog && !this.saving) close();
    });
    const cancelButton = dialog.querySelector<HTMLButtonElement>(
      "[data-cancel-jutsu]",
    )!;
    dialog
      .querySelector<HTMLButtonElement>("[data-confirm-jutsu]")
      ?.addEventListener("click", async (event) => {
        const button = event.currentTarget as HTMLButtonElement;
        const feedback = dialog.querySelector<HTMLElement>(
          ".jutsu-confirm-feedback",
        )!;
        button.disabled = true;
        cancelButton.disabled = true;
        this.saving = true;
        dialog.dataset.saving = "true";
        try {
          await confirm();
          close();
        } catch (error) {
          feedback.textContent = (error as Error).message;
          button.disabled = false;
          cancelButton.disabled = false;
          this.saving = false;
          delete dialog.dataset.saving;
          return;
        }
        this.saving = false;
      });
  }

  private async persist(
    slots: JutsuLoadoutSlot[],
    successMessage: string,
  ): Promise<void> {
    const saved = await this.options.save(this.options.ninjaId, slots);
    if (!this.data) return;
    this.data.equipped = saved;
    this.saving = false;
    if (!this.container.isConnected || !this.options.isActive()) return;
    this.render();
    const feedback =
      this.container.querySelector<HTMLElement>(".jutsu-feedback");
    if (feedback) feedback.textContent = successMessage;
  }
}

function techniqueIcon(id: string) {
  return TECHNIQUE_ICONS[id as keyof typeof TECHNIQUE_ICONS];
}

type TechniqueIconAsset = {
  path: string;
  fallbackPath?: string;
  optional?: boolean;
  width: number;
  height: number;
};

function displayTechniqueId(id: string): string {
  return id
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => {
    const replacements: Record<string, string> = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    };
    return replacements[character] ?? character;
  });
}
